import React, { useEffect, useRef } from "react"
import { graphql } from "gatsby"
import { FooterBarMenuFieldsFragment } from "../../../../../graphql-types"
import * as styles from "./footer-bar-menu.module.scss"
import sanitizeHtml from "sanitize-html"

declare const Didomi: { preferences?: { show?: () => void } } | undefined

declare global {
  interface Window {
    didomiEventListeners?: Array<{ event: string; listener: (...args: unknown[]) => void }>
  }
}

type RenderProps = {
  barMenu: FooterBarMenuFieldsFragment[]
  locale: string
  defaultLocale: string
}

const FooterBarMenu: React.FC<RenderProps> = ({ barMenu, locale, defaultLocale }) => {
  const preferencesButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    barMenu.forEach(x => {
      if (x.__typename === "DatoCmsFooterPage") {
        x.label = x.label.replace(" cookies", '&nbsp;<span lang="en">cookies</span>')
      }
    })
  }, [barMenu])

  useEffect(() => {
    const restoreFocusToTrigger = () => {
      preferencesButtonRef.current?.focus()
    }

    const moveFocusIntoPreferences = () => {
      const popup =
        document.getElementById("didomi-popup") ||
        document.querySelector<HTMLElement>('[id^="didomi-popup"], .didomi-popup-view, .didomi-consent-popup')
      const firstFocusable = popup?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      ;(firstFocusable || popup)?.focus?.()
    }

    const onPreferencesHidden = () => restoreFocusToTrigger()
    const onPreferencesShown = () => {
      window.setTimeout(moveFocusIntoPreferences, 0)
    }

    window.didomiEventListeners = window.didomiEventListeners || []
    window.didomiEventListeners.push(
      { event: "preferences.shown", listener: onPreferencesShown },
      { event: "preferences.hidden", listener: onPreferencesHidden }
    )
  }, [])

  const openCookiePreferences = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault()
    if (typeof Didomi === "undefined" || !Didomi?.preferences?.show) {
      return
    }
    Didomi.preferences.show()
  }

  return (
    <ul className={styles.collapsibleContentBottomMenu}>
      {barMenu.map((barMenuItem, index) =>
        barMenuItem.__typename === "DatoCmsFooterPage" ? (
          <li className={styles.barMenuItem} key={index}>
            {barMenuItem.page ? (
              <a
                href={locale === defaultLocale ? `/${barMenuItem.page.slug}` : `/${locale}/${barMenuItem.page.slug}`}
                className={styles.barMenuLabel}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(barMenuItem.label, { allowedAttributes: { span: ["lang"] } }),
                }}
              ></a>
            ) : (
              <div key={barMenuItem.label} className={styles.barMenuLabel}>
                {barMenuItem.label}
              </div>
            )}
          </li>
        ) : (
          barMenuItem.__typename === "DatoCmsBannerDidomi" && (
            <li className={styles.barMenuItem} key={index}>
              <button
                ref={preferencesButtonRef}
                type="button"
                onClick={openCookiePreferences}
                className={styles.barMenuLabel}
                style={{ backgroundColor: "transparent", border: "none", padding: 0 }}
              >
                {barMenuItem.footerLabel}
              </button>
            </li>
          )
        )
      )}
    </ul>
  )
}

export const fragment = graphql`
  fragment FooterBarMenuFields on DatoCmsUnionForDatoCmsFooterBarMenu {
    ... on DatoCmsFooterPage {
      __typename
      label
      page {
        slug
      }
    }
    ... on DatoCmsBannerDidomi {
      __typename
      footerLabel
    }
  }
`

export default FooterBarMenu
